import { linkedSignal, Resource, Signal } from '@angular/core';
import { ReadonlyListRepository } from '../../repositories/abstraction';
import { cancelableResource } from '../../signals/cancelable-resource';

export interface RepositoryListServiceOptions<T> {
  defaultItems?: T[];
  skipInitialLoad?: boolean;
}

export abstract class ReadonlyRepositoryListService<T, TResult, TParams>
  implements Resource<T[]>
{
  abstract params: Signal<TParams>;

  protected _resource = cancelableResource({
    skipInitialLoad: this.options?.skipInitialLoad ?? false,
    params: () => ({
      params: this.params(),
    }),
    loader: async (param) => {
      return await this.repository.load(param.params.params);
    },
  });

  resource = this._resource.asReadonly();
  isLoading = this._resource.isLoading;
  error = this._resource.error;
  status = this._resource.status;

  all = linkedSignal(() => {
    //TODO: use hasValue method here to prevent endless loop
    if (this._resource.status() === 'error') {
      return this.options?.defaultItems ?? [];
    }
    return this.getItems(this._resource.value());
  });

  value = this.all.asReadonly();

  protected constructor(
    protected repository: ReadonlyListRepository<TResult, TParams>,
    protected options: RepositoryListServiceOptions<T> = {}
  ) {
    if (options?.defaultItems) this.all.set(options.defaultItems);
  }

  load(): boolean {
    if (this._resource.status() === 'idle') return this._resource.reload();
    return false;
  }

  cancel(): void {
    this._resource.cancel();
  }

  reload(): boolean {
    return this._resource.reload();
  }

  clear(): void {
    this._resource.set(undefined);
  }

  reset(): void {
    this._resource.set(undefined);
    this.all.set(this.options?.defaultItems ?? []);
  }

  hasValue(): this is Resource<Exclude<T[], undefined>> {
    return this._resource.hasValue();
  }

  protected abstract getItems(result: TResult | null | undefined): T[];
}
